import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { appointmentsApi, extractOne } from '../../api/facades';

const AppointmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['appointments', numId],
    queryFn: () => appointmentsApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const deleteMu = useMutation({
    mutationFn: () => appointmentsApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setShowDelete(false);
      navigate('/dashboard/appointments');
      toast.success('Appointment deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  if (Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Appointment not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/appointments')}>Back</Button>
      </div>
    );
  }

  const r = row as any;

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{r?.name ?? 'Appointment'}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard/appointments')}>Back</Button>
          <Button variant="primary" onClick={() => navigate(`/dashboard/appointments/${id}/edit`)}>Edit</Button>
          <Button variant="secondary" onClick={() => setShowDelete(true)} className="text-red-600 hover:text-red-700">Delete</Button>
        </div>
      </div>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Type</dt><dd className="text-slate-700 dark:text-slate-200">{r?.type ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Date</dt><dd className="text-slate-700 dark:text-slate-200">{r?.date ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Time</dt><dd className="text-slate-700 dark:text-slate-200">{r?.time ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Client</dt><dd className="text-slate-700 dark:text-slate-200">{r?.client?.name ?? r?.advo_client_id ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Mobile</dt><dd className="text-slate-700 dark:text-slate-200">{r?.mobile ?? '—'}</dd></div>
          <div><dt className="text-xs font-semibold text-slate-500 uppercase">Active</dt><dd className="text-slate-700 dark:text-slate-200">{r?.is_active ?? '—'}</dd></div>
          {r?.note && <div className="md:col-span-2"><dt className="text-xs font-semibold text-slate-500 uppercase">Note</dt><dd className="text-slate-700 dark:text-slate-200">{r.note}</dd></div>}
        </dl>
      )}
      <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteMu.mutate()} title="Delete appointment" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default AppointmentDetailPage;
